import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { documentFingerprint, fingerprint, reloadDecision, shellAssets } from '../lib/appVersion';

// How often to ask. A conditional-free ~1 KB GET over the LAN to the Pi in the same room;
// a minute is not a load, and a release that takes a minute to reach the floor is a
// release that has reached the floor.
const POLL_MS = 60_000;

// How long a terminal has to go untouched before a reload is nobody's interruption.
//
// The same minute the idle lock waits (#308 review). This was 20s, on the reasoning that a
// reload "only costs a repaint" — which is true of the cart, mirrored to localStorage by
// `orderDraft`, and not true of the payment dialog beside it. Firing at 20s with no warning
// made this strictly more aggressive than the lock it claimed to mirror, which waits 60s and
// spends the last 15 of them warning. `busy` below is the rest of that correction.
const IDLE_MS = 60_000;

// Same list as `useIdleLock` watches, and for the same reason: `wheel` is a long menu
// being scrolled, the rest are touch and keys.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'];

// Survives the reload, so the tab can tell "a new release is out" from "I reloaded for
// this exact release a moment ago and I am still being offered it". The second is a bug
// somewhere — a half-rolled deploy, a proxy of someone's own — and a till that reloads
// itself every minute is worse than a till that is one release behind. Per tab, not per
// device: `sessionStorage`.
const TRIED_KEY = 'verlo_version_reload_tried';

const readTried = () => {
  try {
    return sessionStorage.getItem(TRIED_KEY) || '';
  } catch {
    /* private mode / quota — the loop guard is a nicety, not a reason to throw */
    return '';
  }
};

const writeTried = (value) => {
  try {
    sessionStorage.setItem(TRIED_KEY, value);
  } catch {
    /* as above */
  }
};

/**
 * Reload the terminal when the node starts serving a different build (Oct 2026).
 *
 * nginx keeps the shell out of every cache, so any *load* lands on the current release.
 * This is the other half: the tab that is never reloaded. A wall-mounted kiosk is switched
 * on at the start of the week and left, so without this a Pi rolls forward and the screens
 * in the room carry on running the release they booted — against an API that has moved,
 * and naming `/assets/*` chunks the new image does not contain.
 *
 * What counts as a new build is the set of hashed module URLs the server's `index.html`
 * names, against the set this tab booted with (`lib/appVersion`). No stamp to bump and
 * nothing for CI to pass in.
 *
 * When it reloads is `reloadDecision`'s call: at once on a hidden or untouched terminal,
 * and otherwise as a toast the server taps when they are between tables. `busy` is the
 * caller's veto on the automatic half — node-ui passes `lib/reloadSafety`, which holds back
 * on an open dialog and on the kitchen and bar boards, where a reload with no user gesture
 * behind it would leave the new-ticket chime silently dead.
 */
export function useVersionWatch({ pollMs = POLL_MS, idleMs = IDLE_MS, busy } = {}) {
  // The build this tab is running, read once from its own document before React has had a
  // chance to touch anything. Deliberately not re-read: it is the constant we compare to.
  const bootedRef = useRef(null);
  if (bootedRef.current === null) {
    bootedRef.current = typeof document === 'undefined' ? '' : documentFingerprint(document);
  }

  const lastActivityRef = useRef(Date.now());
  const promptedRef = useRef(false);
  // In a ref, so an inline predicate from the caller does not re-create the interval on
  // every render. Read at decision time, which is when its answer is wanted anyway.
  const busyRef = useRef(busy);
  busyRef.current = busy;

  const reloadNow = useCallback((version) => {
    if (version) writeTried(version);
    window.location.reload();
  }, []);

  useEffect(() => {
    const booted = bootedRef.current;
    // Nothing to compare against — a document with no module script is a test renderer or
    // a shell we do not recognise, and guessing would mean reloading a till on a hunch.
    if (!booted) return undefined;

    let cancelled = false;

    const bump = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));

    const check = async () => {
      let html;
      try {
        // `no-store` on the request as well as the response: the point is to read what the
        // node is serving right now, and a conditional request that a cache answers itself
        // would be this whole mechanism agreeing with the thing it exists to catch.
        const res = await fetch('/index.html', { cache: 'no-store', credentials: 'same-origin' });
        if (!res.ok) return;
        html = await res.text();
      } catch {
        // Offline, or the Pi is restarting mid-deploy — which is exactly when this poll
        // runs into it. Say nothing and ask again next tick.
        return;
      }
      if (cancelled) return;

      const served = fingerprint(shellAssets(html));
      if (!served) return; // a captive portal, an error page: not a release.

      const changed = served !== booted;
      const decision = reloadDecision({
        changed,
        alreadyTried: changed && readTried() === served,
        // Asked only when there is something to decide, so a caller's predicate is never
        // run on the common path where nothing has changed.
        busy: changed ? !!busyRef.current?.() : false,
        hidden: typeof document !== 'undefined' && document.hidden,
        msSinceActivity: Date.now() - lastActivityRef.current,
        idleMs,
      });

      if (decision === 'reload') {
        reloadNow(served);
      } else if (decision === 'prompt' && !promptedRef.current) {
        promptedRef.current = true;
        toast('A new version of Verlo is ready', {
          // Until it is taken. The next idle moment takes it anyway, so this is for the
          // terminal that is in constant use and never gets one.
          duration: Infinity,
          action: { label: 'Reload', onClick: () => reloadNow(served) },
        });
      }
    };

    const timer = setInterval(check, pollMs);
    // And whenever the terminal comes back to the foreground — a tab switched to after an
    // hour is the likeliest moment for it to be behind, and the cheapest moment to fix.
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);

    check();

    return () => {
      cancelled = true;
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, [pollMs, idleMs, reloadNow]);
}

export default useVersionWatch;
